import { Utils } from '@client/utility';
import * as Sentry from '@sentry/react';
import { CharacterSimple } from '@components'
import { useUserStore } from '@client/store';

interface FetchCharactersProps {
  currentPage?: number;
  itemsPerPage?: number;
  genderFilter?: string | null;
  tagsFilter?: string[] | null;
  sortBy?: string;
  filterCreatorUUID?: string | null;
  filterCharUUID?: string | null;
  search?: string | null;
  showRecommended?: boolean;
  include_sensitive?: boolean;
}

interface FetchCharactersResponse {
  characters: CharacterSimple[];
  totalItems: number;
  totalPages: number;
  recommendedCharacters: CharacterSimple[];
}

interface BackendCharacter {
  name: string;
  tags: string[];
  title: string;
  gender: string;
  creator: string;
  is_nsfw: boolean;
  persona: string;
  scenario: string;
  attribute: string;
  char_uuid: string;
  is_banned: boolean;
  created_at: string;
  description: string;
  creator_uuid: string;
  first_message: string[];
  profile_image: string;
  chat_messages_count: number;
  type: 'public' | 'private';
}

export async function fetchCharacters({
  currentPage = 1,
  itemsPerPage = 20,
  genderFilter = null,
  tagsFilter = null,
  sortBy = 'created_at',
  filterCreatorUUID = null,
  filterCharUUID = null,
  search = null,
  showRecommended = false,
  include_sensitive = false,
}: FetchCharactersProps): Promise<FetchCharactersResponse> {
  const { include_forbiddens } = useUserStore.getState();

  const body: Record<string, unknown> = {
    limit: itemsPerPage,
    page: currentPage,
    gender: genderFilter,
    tags: tagsFilter && tagsFilter.length > 0 ? tagsFilter : null,
    sort_by: sortBy,
    filter_creator_uuid: filterCreatorUUID,
    filter_char_uuid: filterCharUUID,
    search: search?.trim() || null,
    show_recommended: showRecommended,
    include_forbiddens,
    include_sensitive,
  };

  try {
    const result = await Utils.post<{
      total_count: number;
      total_pages: number;
      current_page: number;
      characters: BackendCharacter[];
      recommended_characters?: BackendCharacter[];
    }>('/api/Characters', body);

    const mapCharacter = (char: BackendCharacter): CharacterSimple => ({
      title: char.title,
      name: char.name,
      description: char.description,
      persona: char.persona,
      model_instructions: char.attribute,
      first_message: char.first_message,
      message_example: char.persona,
      scenario: char.scenario,
      gender: char.gender,
      tags: char.tags,
      lorebook: '',
      attribute: char.attribute,
      creator_notes: undefined,
      gallery: [],
      is_public: char.type === 'public',
      is_nsfw: char.is_nsfw,
      char_uuid: char.char_uuid,
      creator: char.creator,
      creator_uuid: char.creator_uuid,
      profile_image: char.profile_image,
      chat_messages_count: char.chat_messages_count,
    });

    return {
      characters: result.characters?.map(mapCharacter) ?? [],
      totalItems: result.total_count ?? 0,
      totalPages: result.total_pages ?? 1,
      recommendedCharacters: result.recommended_characters?.map(mapCharacter) ?? [],
    };
  } catch (err) {
    if (err instanceof Error) {
      console.error('Error fetching characters:', err);
      Sentry.captureException(err);
    } else {
      console.error('Unknown error fetching characters:', err);
      Sentry.captureMessage('An unknown error occurred in fetchCharacters.');
    }

    return {
      characters: [],
      totalItems: 0,
      totalPages: 1,
      recommendedCharacters: [],
    };
  }
}